import { CSSProperties } from 'react';
import { Page } from '@goldcast/player-config/src/types/remotion-types';
import { CaptionsPosition, CaptionStyles, LayoutType, SizeType } from '@goldcast/player-config/src/types/asset-types';
import { isLayoutAudiogram } from '@goldcast/player-config/src/utils/layout';
import { DEFAULT_FONT_SIZE } from '@goldcast/player-config/src/constants/player';

export type StylesForPage = Pick<
  Page,
  'style' | 'activeTokenStyle' | 'inactiveTokenStyle' | 'groupedTokensStyle' | 'highlightBoxStyle'
>;

const getStylesForAudiogramCaptionsContainer = (size: SizeType): CSSProperties => {
  const commonProps: CSSProperties = {
    position: 'absolute',
    justifyContent: 'flex-start',
    alignItems: 'flex-start'
  };
  if (size === 'LANDSCAPE') {
    const hGap = 2;
    const vGap = 2 * hGap;
    return {
      ...commonProps,
      left: `${50 + hGap}%`,
      top: `${vGap}%`,
      width: `${50 - 2 * hGap}%`,
      height: '40%'
    };
  }
  if (size === 'SQUARE') {
    const gap = 2;
    return {
      ...commonProps,
      left: `${gap}%`,
      top: `${85 + gap}%`,
      width: `${85 - 2 * gap}%`,
      height: `${15 - 2 * gap}%`
    };
  }

  if (size === 'PORTRAIT') {
    const vGap = 2;
    const hGap = 2 * vGap;
    return {
      ...commonProps,
      left: `${hGap}%`,
      top: `${70 + vGap}%`,
      width: `${100 - 2 * hGap}%`,
      height: `${20 - 2 * vGap}%`
    };
  }
  return {};
};

const getGroupedTokensStyleForAudiogram = (size: SizeType): CSSProperties => {
  if (size === 'LANDSCAPE') {
    return { justifyContent: 'flex-start', alignItems: 'flex-start', padding: '0px' };
  }
  if (size === 'SQUARE') {
    return { justifyContent: 'flex-start', alignItems: 'flex-start', padding: '0px', lineHeight: 1.1 };
  }
  return { justifyContent: 'flex-start', alignItems: 'flex-start', padding: '0px' };
};

function getCommonContainerStyles({
  captionStyles,
  captionPosition,
  size,
  layout,
  fontFamily
}: {
  captionStyles: CaptionStyles | undefined;
  captionPosition: CaptionsPosition | undefined;
  size: SizeType;
  layout: LayoutType;
  fontFamily: string;
}): CSSProperties {
  const fontSize = captionStyles?.font_size || DEFAULT_FONT_SIZE;

  const left = captionPosition?.x ?? '2.5%';
  const top = captionPosition?.y ?? '82%';
  const width = captionPosition?.width ?? '95%';
  const height = captionPosition?.height ?? '18%';

  return {
    display: 'flex',
    fontFamily,
    fontSize,
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    left: typeof left !== 'string' ? left + '%' : left,
    top: typeof top !== 'string' ? top + '%' : top,
    width: typeof width !== 'string' ? width + '%' : width,
    height: typeof height !== 'string' ? height + '%' : height,
    textTransform: captionStyles?.caps ? 'uppercase' : 'none',
    ...(isLayoutAudiogram(layout) ? getStylesForAudiogramCaptionsContainer(size) : {})
  };
}

function getGroupedTokensStyle({
  captionStyles,
  size,
  layout,
  fontFamily
}: {
  captionStyles: CaptionStyles | undefined;
  size: SizeType;
  layout: LayoutType;
  fontFamily: string;
}): CSSProperties {
  const fontSize = captionStyles?.font_size || (captionStyles?.scale || 1) * DEFAULT_FONT_SIZE;

  const commonContainerStyles: CSSProperties = {
    display: 'flex',
    fontFamily,
    fontSize,
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center'
  };

  return {
    ...commonContainerStyles,
    lineHeight: fontSize * 1.5 + 'px',
    ...(isLayoutAudiogram(layout) ? getGroupedTokensStyleForAudiogram(size) : {}),
    ...captionStyles?.pageStyle
  };
}

function getActiveTokenStyle(captionStyles?: CaptionStyles): CSSProperties {
  if (!captionStyles) {
    return {};
  }

  return {
    fontFamily: 'inherit',
    lineHeight: 'inherit',
    ...captionStyles.wordStyle?.active
  };
}

function getInactiveTokenStyle(captionStyles?: CaptionStyles): CSSProperties {
  if (!captionStyles) {
    return {};
  }

  return {
    fontFamily: 'inherit',
    lineHeight: 'inherit',
    ...captionStyles.wordStyle?.inactive
  };
}

export function getStylesForPage({
  captionStyles,
  captionPosition,
  size,
  layout,
  fontFamily
}: {
  captionStyles: CaptionStyles | undefined;
  captionPosition: CaptionsPosition | undefined;
  size: SizeType;
  layout: LayoutType;
  fontFamily: string;
}): StylesForPage {
  return {
    style: getCommonContainerStyles({ captionStyles, captionPosition, size, layout, fontFamily }),
    groupedTokensStyle: getGroupedTokensStyle({ captionStyles, size, layout, fontFamily }),
    activeTokenStyle: getActiveTokenStyle(captionStyles),
    inactiveTokenStyle: getInactiveTokenStyle(captionStyles),
    highlightBoxStyle:
      captionStyles?.wordStyle?.highlightType === 'box' ? captionStyles?.wordStyle?.highlightBoxStyle : undefined
  };
}
